import React from 'react';
import { cn } from '../../lib/utils';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Plus } from 'lucide-react';
import { usePromoCodes } from './hooks/usePromoCodes';
import { PromoCodesTable, PromoCodeDialog } from './components';
import { PromoCodesPageProps } from './types';

export default function PromoCodesPage({ isDark }: PromoCodesPageProps) {
  const {
    promoCodes,
    isLoading,
    includeInactive,
    isCreateDialogOpen,
    isEditDialogOpen,
    formData,
    isSubmitting,
    errors,
    clearFieldError,
    setIncludeInactive,
    setIsCreateDialogOpen,
    setIsEditDialogOpen,
    setFormData,
    handleCreate,
    handleEdit,
    handleUpdate,
    handleDelete,
  } = usePromoCodes();

  const formatDate = (date: string | null) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatValue = (type: string, value: number) => {
    if (type === 'percent') return `${value}%`;
    return `${value} UZS`;
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={cn(
            'text-2xl lg:text-3xl font-bold tracking-tight',
            isDark ? 'text-white' : 'text-[#111111]'
          )}>Промокоды</h1>
          <p className={cn(
            'text-sm mt-1',
            isDark ? 'text-slate-400' : 'text-slate-500'
          )}>Управление промокодами</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Switch checked={includeInactive} onCheckedChange={setIncludeInactive} />
            <Label className={cn('text-sm', isDark ? 'text-slate-300' : 'text-slate-700')}>Показать неактивные</Label>
          </div>
          <Button onClick={() => setIsCreateDialogOpen(true)} className="bg-[#C9A227] hover:bg-[#C9A227]/90">
            <Plus className="w-4 h-4 mr-2" />
            Создать промокод
          </Button>
        </div>
      </div>

      {/* Promo Codes Table */}
      <Card className={cn(
        'rounded-2xl border-0 overflow-hidden transition-all duration-300',
        isDark 
          ? 'bg-[#1A1A22] shadow-lg shadow-black/10' 
          : 'bg-white shadow-lg shadow-slate-200/50'
      )}>
        <PromoCodesTable
          isDark={isDark}
          promoCodes={promoCodes}
          isLoading={isLoading}
          onEdit={handleEdit}
          onDelete={handleDelete}
          formatDate={formatDate}
          formatValue={formatValue}
        />
      </Card>

      {/* Create Dialog */}
      <PromoCodeDialog
        isDark={isDark}
        isOpen={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        formData={formData}
        onFormDataChange={setFormData}
        onSubmit={handleCreate}
        isSubmitting={isSubmitting}
        errors={errors}
        onClearFieldError={clearFieldError}
        title="Создать промокод"
        description="Добавить новый промокод"
        submitLabel="Создать"
      />

      {/* Edit Dialog */}
      <PromoCodeDialog
        isDark={isDark}
        isOpen={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        formData={formData}
        onFormDataChange={setFormData}
        onSubmit={handleUpdate}
        isSubmitting={isSubmitting}
        errors={errors}
        onClearFieldError={clearFieldError}
        title="Редактировать промокод"
        description="Обновить промокод"
        submitLabel="Сохранить"
      />
    </div>
  );
}
